import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Folder as FolderIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  VideoLibrary as VideoIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const HRDocuments = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'grid', 'analytics'
  const [currentFolder, setCurrentFolder] = useState('root');
  
  // Data states
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  // Upload dialog form state (FB-072)
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadEmployeeId, setUploadEmployeeId] = useState('');
  const [uploadDocumentType, setUploadDocumentType] = useState('general');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Document preview state (FB-068)
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    document_name: '',
    document_type: '',
    description: '',
    employee_id: '',
    folder_id: '',
    is_public: false,
    tags: '',
    file: null
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    type: 'all',
    employee: 'all',
    folder: 'all',
    search: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDocuments(),
        loadFolders(),
        loadEmployees(),
        loadStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // TKT-256: previously this fell back to getMockDocuments() whenever the
  // list endpoint failed -- which it always did, because the backend was
  // querying a table that doesn't exist. That fake data (a phantom
  // "Company Logo.png") is exactly what Naomi saw, and its id couldn't be
  // downloaded or previewed. Show the real list, including a real empty
  // state, instead of papering over a backend error with fake rows.
  const loadDocuments = async () => {
    try {
      const response = await makeApiCall('api/hr/documents');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setDocuments([]);
    }
  };

  // TKT-256: employee_documents has no folder model, so there's no
  // /folders endpoint to hit. Keep a single static Root entry purely so
  // the breadcrumb / New-Document folder picker still render.
  const loadFolders = async () => {
    setFolders([{ id: 'root', name: 'Root', parent_id: null, path: '/' }]);
  };

  const loadEmployees = async () => {
    try {
      // TKT-270: /api/employees paginates at 10 by default, so employee
      // dropdowns on the upload dialog only showed the first page.
      const response = await makeApiCall('api/employees?page=1&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  // TKT-256: the cards read `stats.*`, so the endpoint must hand back a
  // flat `stats` object. The mock fallback used to hide the fact that the
  // old response shape ({ overall, by_type }) never populated the cards,
  // leaving them at 0 even with rows present.
  const loadStats = async () => {
    try {
      const response = await makeApiCall('api/hr/documents/stats/overview');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      } else {
        setStats({});
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats({});
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <PdfIcon color="error" />;
      case 'document': return <DocumentIcon color="primary" />;
      case 'image': return <ImageIcon color="success" />;
      case 'video': return <VideoIcon color="secondary" />;
      default: return <FileIcon color="disabled" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setFormData({
      document_name: document.document_name,
      document_type: document.document_type,
      description: document.description,
      employee_id: document.employee_id || '',
      folder_id: document.folder_id,
      is_public: document.is_public,
      tags: document.tags,
      file: null
    });
    setOpenDialog(true);
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
  };

  const handleUploadDocument = () => {
    setOpenUploadDialog(true);
  };

  const handleSaveDocument = async () => {
    try {
      const url = editingDocument ? `api/hr/documents/${editingDocument.id}` : 'api/hr/documents';
      const method = editingDocument ? 'PUT' : 'POST';
      
      const response = await makeApiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSuccess(editingDocument ? 'Document updated successfully' : 'Document created successfully');
        setOpenDialog(false);
        loadDocuments();
      } else {
        throw new Error('Failed to save document');
      }
    } catch (err) {
      setError('Failed to save document');
    }
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await makeApiCall(`api/hr/documents/${documentId}/download`);
      if (!response.ok) {
        // TKT-206: surface the real backend error so the user can
        // tell whether it was a missing s3_key, missing S3 creds, or
        // permissions instead of a generic "Failed to fetch download
        // URL" message.
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to fetch download URL (HTTP ${response.status})`);
      }
      const data = await response.json();
      if (!data.download_url) {
        throw new Error('No download URL returned by server');
      }
      // TKT-233: the signed URL now carries attachment disposition, so a
      // same-tab anchor click downloads in place — no new tab on iPad.
      const link = window.document.createElement('a');
      link.href = data.download_url;
      link.rel = 'noopener noreferrer';
      if (data.filename) {
        link.download = data.filename;
      }
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setSuccess('Document download started');
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err.message || 'Failed to download document');
    }
  };

  const fetchSignedUrl = async (documentId) => {
    try {
      const response = await makeApiCall(`api/hr/documents/${documentId}/download`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.download_url || null;
    } catch (err) {
      console.error('Error fetching signed URL:', err);
      return null;
    }
  };

  // TKT-198: Naomi wants staff to preview Company Documents (DOCX,
  // XLSX, etc.) on iPad/phone without forcing a download every time.
  // Image and PDF render natively in the dialog; anything else with a
  // recognised MIME type now falls through to the Google Docs Viewer
  // wrapper handled by the preview iframe below.
  const isPreviewableType = (mimeType, documentType) => {
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType === 'application/pdf') return 'pdf';
    }
    if (documentType === 'image') return 'image';
    if (documentType === 'pdf') return 'pdf';
    const officeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/csv',
      'application/rtf',
    ];
    if (mimeType && officeTypes.includes(mimeType)) return 'office';
    if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'csv', 'rtf'].includes(documentType)) {
      return 'office';
    }
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    const loadPreview = async () => {
      if (!selectedDocument?.id) {
        setPreviewUrl(null);
        return;
      }
      const previewType = isPreviewableType(selectedDocument.mime_type, selectedDocument.document_type);
      if (!previewType) {
        setPreviewUrl(null);
        return;
      }
      setPreviewLoading(true);
      const url = await fetchSignedUrl(selectedDocument.id);
      if (!cancelled) {
        setPreviewUrl(url);
        setPreviewLoading(false);
      }
    };
    loadPreview();
    return () => { cancelled = true; };
  }, [selectedDocument]);

  const handleUploadFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setUploadFile(file || null);
    if (file && !uploadDescription) {
      setUploadDescription(file.name);
    }
  };

  const handleSubmitUpload = async () => {
    if (!uploadFile) {
      setError('Please choose a file to upload.');
      return;
    }
    try {
      setUploading(true);
      // TKT-255: employee_id is now optional. Only append it to the
      // form data when the user actually picked an employee; the
      // backend stores company-wide rows (employee_id NULL) under
      // hr-documents/company/ when omitted.
      const formPayload = new FormData();
      formPayload.append('file', uploadFile);
      if (uploadEmployeeId) {
        formPayload.append('employee_id', uploadEmployeeId);
      }
      formPayload.append('document_type', uploadDocumentType || 'general');
      formPayload.append('description', uploadDescription || uploadFile.name);

      const response = await makeApiCall('api/hr/documents/upload', {
        method: 'POST',
        body: formPayload
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Upload failed');
      }

      setSuccess('Document uploaded successfully');
      setOpenUploadDialog(false);
      setUploadFile(null);
      setUploadEmployeeId('');
      setUploadDocumentType('general');
      setUploadDescription('');
      loadDocuments();
      loadStats();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Box>
        <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
          HR Documents
        </Typography>
        <Breadcrumbs sx={{ mt: 1 }}>
          <Link color="inherit" href="#" onClick={() => setCurrentFolder('root')}>
            Root
          </Link>
          {currentFolder !== 'root' && (
            <Typography color="text.primary">
              {folders.find(f => f.id === currentFolder)?.name || 'Current Folder'}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          size="small"
        >
          <ToggleButton value="list">List</ToggleButton>
          <ToggleButton value="grid">Grid</ToggleButton>
          <ToggleButton value="analytics">Analytics</ToggleButton>
        </ToggleButtonGroup>
        {/* TKT-256: the old "New Document" button POSTed metadata-only
            rows to a non-existent hr_documents table and could never
            attach a file. Upload is the only real way to add a document,
            so the dead button is gone. */}
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleUploadDocument}
        >
          Upload
        </Button>
      </Box>
    </Box>
  );

  const renderStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.total_documents || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Documents
                </Typography>
              </Box>
              <DocumentIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {formatFileSize(stats.total_size || 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Size
                </Typography>
              </Box>
              <CloudUploadIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.public_documents || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Public Documents
                </Typography>
              </Box>
              <PublicIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.private_documents || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Private Documents
                </Typography>
              </Box>
              <LockIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Documents"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                label="Document Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="document">Document</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // TKT-256: employee_documents rows have no folder_id, so the old
  // `doc.folder_id !== currentFolder` check (currentFolder defaults to
  // 'root') excluded every real document and left the list empty. Treat
  // 'root' and 'all' as "no folder filter" since the folder model isn't
  // backed by data.
  const matchesFolder = (doc) =>
    currentFolder === 'all' || currentFolder === 'root' || doc.folder_id === currentFolder;

  const filterDocuments = () => {
    return documents.filter(doc => {
      const name = (doc.document_name || doc.filename || '').toLowerCase();
      if (filters.search && !name.includes(filters.search.toLowerCase()) &&
          !(doc.description || '').toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.type !== 'all' && doc.document_type !== filters.type) return false;
      if (filters.employee !== 'all' && doc.employee_id !== parseInt(filters.employee)) return false;
      if (!matchesFolder(doc)) return false;
      return true;
    });
  };

  // FB-071: Grid view (card thumbnails)
  const renderGridView = () => {
    const filteredDocuments = filterDocuments();
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Documents</Typography>
          {filteredDocuments.length === 0 ? (
            <Alert severity="info">No documents match the current filters.</Alert>
          ) : (
            <Grid container spacing={2}>
              {filteredDocuments.map((document) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={document.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                    }}
                    onClick={() => handleViewDocument(document)}
                    role="button"
                    aria-label={`Open ${document.document_name}`}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="center" mb={2} sx={{ fontSize: '3rem' }}>
                        {React.cloneElement(getFileIcon(document.document_type), { sx: { fontSize: 64 } })}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="medium"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.5
                        }}
                        title={document.document_name}
                      >
                        {document.document_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatFileSize(document.file_size || 0)} - {(document.document_type || '').toUpperCase()}
                      </Typography>
                      {document.employee_name && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                          <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                            {document.employee_name.charAt(0)}
                          </Avatar>
                          <Typography variant="caption" noWrap>
                            {document.employee_name}
                          </Typography>
                        </Box>
                      )}
                      <Box display="flex" gap={0.5} mt={1}>
                        <Chip
                          icon={document.is_public ? <PublicIcon /> : <LockIcon />}
                          label={document.is_public ? 'Public' : 'Private'}
                          color={document.is_public ? 'success' : 'warning'}
                          size="small"
                        />
                        {document.folder_name && (
                          <Chip
                            label={document.folder_name}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>
                    <DialogActions sx={{ pt: 0 }}>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadDocument(document.id);
                          }}
                          aria-label={`Download ${document.document_name}`}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDocument(document);
                          }}
                          aria-label={`Edit ${document.document_name}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DialogActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  // FB-071: Analytics view
  const renderAnalyticsView = () => {
    const totalDocs = Number(stats.total_documents || 0);
    const byType = stats.documents_by_type || {};
    const typeEntries = Object.entries(byType);
    const maxTypeCount = typeEntries.reduce((m, [, v]) => Math.max(m, Number(v || 0)), 0) || 1;
    const publicPct = totalDocs ? (Number(stats.public_documents || 0) / totalDocs) * 100 : 0;

    return (
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Storage Overview</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Total Documents</Typography>
                    <Typography variant="h4" fontWeight="bold">{totalDocs}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Total Size</Typography>
                    <Typography variant="h4" fontWeight="bold">{formatFileSize(stats.total_size || 0)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Recent Uploads</Typography>
                    <Typography variant="h4" color="info.main" fontWeight="bold">{stats.recent_uploads || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Storage Used</Typography>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {stats.storage_used ? `${stats.storage_used} MB` : formatFileSize(stats.total_size || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Public vs Private</Typography>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Chip icon={<PublicIcon />} label={`${stats.public_documents || 0} Public`} color="success" />
              <Chip icon={<LockIcon />} label={`${stats.private_documents || 0} Private`} color="warning" />
            </Box>
            <LinearProgress
              variant="determinate"
              value={publicPct}
              color="success"
              sx={{ height: 12, borderRadius: 1 }}
              aria-label={`${publicPct.toFixed(0)}% public documents`}
            />
            <Typography variant="caption" color="textSecondary">
              {publicPct.toFixed(0)}% public
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Documents by Type</Typography>
            {typeEntries.length === 0 ? (
              <Alert severity="info">No type breakdown available yet.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {typeEntries.map(([type, count]) => (
                  <Box key={type}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFileIcon(type)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{type}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">{count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(Number(count || 0) / maxTypeCount) * 100}
                      sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                      aria-label={`${count} ${type} documents`}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderDocumentsList = () => {
    const filteredDocuments = filterDocuments();

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Folder</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No documents found. Use the Upload button to add one.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
                {filteredDocuments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((document) => (
                    <TableRow key={document.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getFileIcon(document.document_type)}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {document.document_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {document.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(document.document_type || 'general').toUpperCase()}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatFileSize(document.file_size)}</TableCell>
                      <TableCell>
                        {document.employee_name ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {document.employee_name.charAt(0)}
                            </Avatar>
                            {document.employee_name}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            General
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={document.folder_name}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={document.is_public ? <PublicIcon /> : <LockIcon />}
                          label={document.is_public ? 'Public' : 'Private'}
                          color={document.is_public ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(document.uploaded_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          by {document.uploaded_by}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleViewDocument(document)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownloadDocument(document.id)}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditDocument(document)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDocuments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const renderViewDialog = () => {
    if (!selectedDocument) return null;
    const previewType = isPreviewableType(selectedDocument.mime_type, selectedDocument.document_type);
    return (
      <Dialog
        open={Boolean(selectedDocument)}
        onClose={() => setSelectedDocument(null)}
        maxWidth="md"
        fullWidth
        aria-labelledby="document-view-dialog-title"
      >
        <DialogTitle id="document-view-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            {getFileIcon(selectedDocument.document_type)}
            <Typography component="span" variant="h6" sx={{ wordBreak: 'break-word' }}>
              {selectedDocument.document_name || selectedDocument.filename || 'Document Details'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Document Type</Typography>
                <Typography variant="body2">
                  {(selectedDocument.document_type || '').toUpperCase() || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">File Size</Typography>
                <Typography variant="body2">{formatFileSize(selectedDocument.file_size || 0)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Employee</Typography>
                <Typography variant="body2">{selectedDocument.employee_name || 'General Document'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Folder</Typography>
                <Typography variant="body2">{selectedDocument.folder_name || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Uploaded</Typography>
                <Typography variant="body2">
                  {selectedDocument.uploaded_at ? new Date(selectedDocument.uploaded_at).toLocaleString() : '-'}
                  {selectedDocument.uploaded_by ? ` by ${selectedDocument.uploaded_by}` : ''}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary">Visibility</Typography>
                <Box mt={0.5}>
                  <Chip
                    icon={selectedDocument.is_public ? <PublicIcon /> : <LockIcon />}
                    label={selectedDocument.is_public ? 'Public' : 'Private'}
                    color={selectedDocument.is_public ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              {selectedDocument.description && (
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedDocument.description}
                  </Typography>
                </Grid>
              )}
              {selectedDocument.tags && (
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Tags</Typography>
                  <Typography variant="body2">{selectedDocument.tags}</Typography>
                </Grid>
              )}
            </Grid>
            <Divider />
            <Box>
              <Typography variant="caption" color="textSecondary">Preview</Typography>
              {previewLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress size={32} />
                </Box>
              ) : previewType === 'image' && previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt={selectedDocument.document_name || 'Document preview'}
                  sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', mt: 1, borderRadius: 1, border: '1px solid #e0e0e0' }}
                />
              ) : previewType === 'pdf' && previewUrl ? (
                <Box
                  component="iframe"
                  src={previewUrl}
                  title={selectedDocument.document_name || 'Document preview'}
                  sx={{ width: '100%', height: 520, mt: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}
                />
              ) : previewType === 'office' && previewUrl ? (
                /* TKT-198: render Office docs through Google Docs Viewer
                   so iPad/phone users can review without downloading. */
                <Box
                  component="iframe"
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                  title={selectedDocument.document_name || 'Document preview'}
                  sx={{ width: '100%', height: 520, mt: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}
                />
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Preview not available for this file type. Use the Download button to open it.
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDocument(null)} aria-label="Close document details">
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadDocument(selectedDocument.id)}
            aria-label={`Download ${selectedDocument.document_name || 'document'}`}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderUploadDialog = () => (
    <Dialog
      open={openUploadDialog}
      onClose={() => !uploading && setOpenUploadDialog(false)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="document-upload-dialog-title"
    >
      <DialogTitle id="document-upload-dialog-title">
        Upload Document
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
            aria-label="Choose file to upload"
          >
            {uploadFile ? `Selected: ${uploadFile.name}` : 'Choose File'}
            <input
              type="file"
              hidden
              onChange={handleUploadFileChange}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt"
            />
          </Button>
          {uploadFile && (
            <Typography variant="caption" color="textSecondary">
              Size: {formatFileSize(uploadFile.size)} - Type: {uploadFile.type || 'unknown'}
            </Typography>
          )}
          {/* TKT-255: Employee is optional now (backend already accepts
              null employee_id under hr-documents/company/). Drop the
              `required` flag and add a leading "Company-wide (no
              employee)" option so company files like Company Logo /
              Policies can be uploaded without picking a person. */}
          <FormControl fullWidth>
            <InputLabel id="upload-employee-label">Employee (optional)</InputLabel>
            <Select
              labelId="upload-employee-label"
              value={uploadEmployeeId}
              onChange={(e) => setUploadEmployeeId(e.target.value)}
              label="Employee (optional)"
              inputProps={{ 'aria-label': 'Select employee for this document, or leave blank for a company-wide upload' }}
            >
              <MenuItem value="">
                <em>Company-wide (no employee)</em>
              </MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.employee_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="upload-doctype-label">Document Type</InputLabel>
            <Select
              labelId="upload-doctype-label"
              value={uploadDocumentType}
              onChange={(e) => setUploadDocumentType(e.target.value)}
              label="Document Type"
              inputProps={{ 'aria-label': 'Select document type' }}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="policy">Policy</MenuItem>
              <MenuItem value="performance">Performance</MenuItem>
              <MenuItem value="discipline">Discipline</MenuItem>
              <MenuItem value="training">Training</MenuItem>
              <MenuItem value="medical">Medical</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            inputProps={{ 'aria-label': 'Document description' }}
          />
          {uploading && <LinearProgress aria-label="Upload in progress" />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOpenUploadDialog(false)}
          disabled={uploading}
          aria-label="Cancel upload"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleSubmitUpload}
          disabled={uploading || !uploadFile}
          aria-label="Submit document upload"
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDocumentDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingDocument ? 'Edit Document' : 'New Document'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Document Name"
              value={formData.document_name}
              onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={formData.document_type}
                onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                label="Document Type"
              >
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="document">Document</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                label="Employee"
              >
                <MenuItem value="">General Document</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Folder</InputLabel>
              <Select
                value={formData.folder_id}
                onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                label="Folder"
              >
                {folders.map((folder) => (
                  <MenuItem key={folder.id} value={folder.id}>
                    {folder.path}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="Enter tags separated by commas"
            />
          </Grid>
          <Grid size={12}>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Make this document public (visible to all employees)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveDocument}>
          {editingDocument ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderHeader()}
      {renderStats()}
      {view !== 'analytics' && renderFilters()}
      {view === 'list' && renderDocumentsList()}
      {view === 'grid' && renderGridView()}
      {view === 'analytics' && renderAnalyticsView()}
      {renderDocumentDialog()}
      {renderViewDialog()}
      {renderUploadDialog()}
    </Box>
  );
};

export default HRDocuments;
